import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { TuiPopover } from '@taiga-ui/cdk';
import { TuiBreakpointService, TuiDialogService } from '@taiga-ui/core';
import { TUI_CONFIRM, TuiConfirmData } from '@taiga-ui/kit';
import { map } from 'rxjs';

import { SidePageOptions } from './sidepage-options';

// Контекст SidePage, как его видит содержимое (у форм он типизирован как TuiDialogContext)
type SidePageCloseContext = Pick<TuiPopover<SidePageOptions, boolean>, 'id' | 'completeWith'> &
  Pick<SidePageOptions, 'confirmClose'>;

/**
 * Закрытие SidePage пользователем без сохранения: крестик, «Отменить» формы, жест «назад».
 * Формы, открытые с `confirmClose`, на телефоне сначала спрашивают подтверждение — там
 * форму легко закрыть случайно. На desktop и без `confirmClose` закрывается сразу.
 * Закрытия из кода (после сохранения, ошибки) идут мимо, через `completeWith`.
 */
@Injectable({ providedIn: 'root' })
export class SidePageCloseService {
  private readonly dialogs = inject(TuiDialogService);
  private readonly translate = inject(TranslateService);

  // Граница 'mobile' у Taiga, как у мобильной раскладки форм
  private readonly isMobile = toSignal(
    inject(TuiBreakpointService).pipe(map((breakpoint) => breakpoint === 'mobile')),
    { initialValue: false },
  );

  // Формы, по которым подтверждение уже открыто: повторный крестик или «назад» его не дублируют
  private readonly confirming = new Set<string>();

  requestClose(context: SidePageCloseContext): void {
    if (!context.confirmClose || !this.isMobile()) {
      context.completeWith(false);
      return;
    }

    if (this.confirming.has(context.id)) return;

    this.confirming.add(context.id);

    const data: TuiConfirmData = {
      content: this.translate.instant('Unsaved changes will be lost'),
      yes: this.translate.instant('Close'),
      no: this.translate.instant('Continue editing'),
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, {
        label: this.translate.instant('Close without saving?'),
        size: 's',
        data,
      })
      .subscribe({
        next: (close) => close && context.completeWith(false),
        complete: () => this.confirming.delete(context.id),
      });
  }
}
